import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton,
  Box,
} from "@mui/material";
import config from "../../utils/config";
import { toast } from "react-toastify";
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "draft-js-import-html";
import { Editor } from "react-draft-wysiwyg";
import { EditorState } from "draft-js";
import { useState, useEffect } from "react";
import axios from "axios";
import { useDebouncedCallback } from "use-debounce";
import AddIcon from "@mui/icons-material/Add";
import AddQuestion from "./AddQuestion";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

function OrgSettings(props) {
  const [name, setName] = useState("");
  const [id, setId] = useState("");
  const [esnCardPrice, setEsnCardPrice] = useState(null);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [onlinePaymentService, setOnlinePaymentService] = useState("none");
  const [stripeSecret, setStripeSecret] = useState("");
  const [stripePublic, setStripePublic] = useState("");
  const [stripeWebhookSecret, setStripeWebhookSecret] = useState("");
  const [redsysMerchantCode, setRedsysMerchantCode] = useState("");
  const [redsysSecret, setRedsysSecret] = useState("");
  const [redsysTerminal, setRedsysTerminal] = useState("");
  const [redsysSandboxMode, setRedsysSandboxMode] = useState(false);
  const [vendusKey, setVendusKey] = useState("");
  const [vendusRegister, setVendusRegister] = useState(null);
  const [vendusPaymentMethod, setVendusPaymentMethod] = useState(null);
  const [registers, setRegisters] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showVAT, setShowVAT] = useState(false);
  const [extraQuestions, setExtraQuestions] = useState([]);
  const [addingQuestion, setAddingQuestion] = useState(false);
  const [editQuestionIndex, setEditQuestionIndex] = useState(null);
  const [anonymizeAfterMonths, setAnonymizeAfterMonths] = useState("");

  useEffect(() => {
    props.setTitle("Organization Settings");
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchOrgInfo();
  }, [props.organization]); // eslint-disable-line

  useEffect(() => {
    if (vendusKey !== "") {
      fetchRegisters();
      fetchPaymentMethods();
    }
  }, [vendusKey]); // eslint-disable-line

  const fetchRegisters = useDebouncedCallback(() => {
    axios
      .get(`${config.API_ENDPOINT}/utils/vendus-registers?apiKey=${vendusKey}`, {
        headers: { "x-access-token": localStorage.getItem("access-token") },
      })
      .then((res) => setRegisters(res.data))
      .catch(() => toast.error("An unexpected error has occurred (VENDUS)"));
  }, 1000);

  const fetchPaymentMethods = useDebouncedCallback(() => {
    axios
      .get(`${config.API_ENDPOINT}/utils/vendus-paymentmethods?apiKey=${vendusKey}`, {
        headers: { "x-access-token": localStorage.getItem("access-token") },
      })
      .then((res) => setPaymentMethods(res.data))
      .catch(() => toast.error("An unexpected error has occurred (VENDUS)"));
  }, 1000);

  const update = useDebouncedCallback(
    () => {
      if (esnCardPrice == null || esnCardPrice < 0) {
        toast.error("Invalid ESNcard price");
        return;
      }

      if (vendusKey !== "" && !vendusRegister) {
        toast.error("Invalid Vendus register");
        return;
      }

      if (vendusKey !== "" && !vendusPaymentMethod) {
        toast.error("Invalid Vendus payment method");
        return;
      }

      if (onlinePaymentService === "stripe") {
        if (stripePublic === "" || stripeSecret === "" || stripeWebhookSecret === "") {
          toast.error("Invalid Stripe settings");
          return;
        }
      }

      if (onlinePaymentService === "redsys") {
        if (redsysMerchantCode === "" || redsysSecret === "" || redsysTerminal === "") {
          toast.error("Invalid Redsys settings");
          return;
        }
      }

      let org = {
        esnCardPrice,
        onlinePaymentService,
        stripePublic,
        stripeSecret,
        stripeWebhookSecret,
        redsysMerchantCode,
        redsysSecret,
        redsysTerminal,
        redsysSandboxMode,
        vendusKey,
        showVAT,
        customEsnCardQuestions: extraQuestions,
        anonymizeAfterMonths: anonymizeAfterMonths !== "" ? anonymizeAfterMonths : 0,
      };

      if (vendusRegister != null) {
        org.vendusRegister = vendusRegister;
      }

      if (vendusPaymentMethod != null) {
        org.vendusPaymentMethod = vendusPaymentMethod;
      }

      if (editorState != null) {
        org.termsConditions = stateToHTML(editorState.getCurrentContent());
      }

      axios
        .post(`${config.API_ENDPOINT}/organizations/${props.organization}`, org, {
          headers: { "x-access-token": localStorage.getItem("access-token") },
        })
        .then(() => toast.success("Success!"))
        .catch((err) => {
          switch (err.response?.status) {
            case 401:
              localStorage.removeItem("access-token");
              window.location.replace("/login");
              break;
            default:
              toast.error("An unexpected error has occurred");
              break;
          }
        });
    },
    500,
    { leading: true }
  );

  const fetchOrgInfo = () => {
    axios
      .get(`${config.API_ENDPOINT}/organizations/secret/${props.organization}`, {
        headers: { "x-access-token": localStorage.getItem("access-token") },
      })
      .then((res) => {
        let org = res.data;
        setName(org.name);
        setId(org._id);
        setEsnCardPrice(org.esnCardPrice);
        setEditorState(EditorState.createWithContent(stateFromHTML(org.termsConditions)));
        setOnlinePaymentService(org.onlinePaymentService);
        setStripeSecret(org.stripeSecret);
        setStripeWebhookSecret(org.stripeWebhookSecret);
        setStripePublic(org.stripePublic);
        setRedsysMerchantCode(org.redsysMerchantCode);
        setRedsysSecret(org.redsysSecret);
        setRedsysTerminal(org.redsysTerminal);
        setRedsysSandboxMode(org.redsysSandboxMode);
        setVendusKey(org.vendusKey);
        setVendusRegister(org.vendusRegister);
        setVendusPaymentMethod(org.vendusPaymentMethod);
        setShowVAT(org.showVAT);
        setExtraQuestions(org.customEsnCardQuestions ?? []);
        setAnonymizeAfterMonths(org.anonymizeAfterMonths > 0 ? org.anonymizeAfterMonths : "");
      })
      .catch((err) => {
        switch (err.response?.status) {
          case 401:
            localStorage.removeItem("access-token");
            window.location.replace("/login");
            break;
          default:
            toast.error("An unexpected error has occurred");
            break;
        }
      });
  };

  if (props.user.accessLevel > 1) return null;

  return (
    <Box sx={{ pb: 3 }}>
      <Box sx={{ mb: 4, pb: 3, borderBottom: "1px solid rgba(19, 33, 68, 0.1)" }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Organization Settings</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Manage organization details, payment methods, and student registration questions
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Basic Information</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>ID: {id}</Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, fontSize: 28 }}>{name}</Typography>
          <TextField
            variant="outlined"
            fullWidth
            label="ESNcard Price (€)"
            name="esnCardPrice"
            type="number"
            value={esnCardPrice || ""}
            required
            onChange={(e) =>
              setEsnCardPrice(e.target.value !== "" ? parseFloat(e.target.value) : null)
            }
            inputProps={{ step: "0.01" }}
            sx={{ mt: 2 }}
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Terms & Conditions</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Editor for terms shown during student registration
          </Typography>
          <Box sx={{ border: "1px solid rgba(19, 33, 68, 0.12)", borderRadius: 1, p: 1.5 }}>
            <Editor
              editorStyle={{ minHeight: 200 }}
              editorState={editorState}
              label="Terms and Conditions..."
              onEditorStateChange={(s) => setEditorState(s)}
            />
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Online Payment Integration</Typography>
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="payment-service-label">Payment Service Provider</InputLabel>
              <Select
                labelId="payment-service-label"
                label="Payment Service Provider"
                value={onlinePaymentService}
                onChange={(e) => setOnlinePaymentService(e.target.value)}
              >
                <MenuItem value="none">None (Disable online payments)</MenuItem>
                <MenuItem value="stripe">Stripe</MenuItem>
                <MenuItem value="redsys">Redsys</MenuItem>
              </Select>
            </FormControl>

            {onlinePaymentService === "stripe" && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}>Stripe Configuration</Typography>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Stripe Public Key"
                  name="stripePublic"
                  type="text"
                  value={stripePublic}
                  required
                  onChange={(e) => setStripePublic(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Stripe Secret Key"
                  name="stripeSecret"
                  type="password"
                  value={stripeSecret}
                  required
                  onChange={(e) => setStripeSecret(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Stripe Webhook Secret"
                  name="stripeWebhookSecret"
                  type="password"
                  value={stripeWebhookSecret}
                  required
                  onChange={(e) => setStripeWebhookSecret(e.target.value)}
                />
              </Box>
            )}

            {onlinePaymentService === "redsys" && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}>Redsys Configuration</Typography>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Redsys Merchant Code"
                  name="redsysMerchantCode"
                  type="text"
                  value={redsysMerchantCode}
                  onChange={(e) => setRedsysMerchantCode(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Redsys Secret"
                  name="redsysSecret"
                  type="password"
                  value={redsysSecret}
                  onChange={(e) => setRedsysSecret(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Redsys Terminal Number"
                  name="redsysTerminal"
                  type="number"
                  value={redsysTerminal}
                  onChange={(e) => setRedsysTerminal(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={redsysSandboxMode}
                      onChange={(e) => setRedsysSandboxMode(e.target.checked)}
                    />
                  }
                  label="Enable Sandbox Mode (for testing)"
                />
              </Box>
            )}
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 2 }}>Vendus Integration (Optional)</Typography>
          <TextField
            variant="outlined"
            fullWidth
            label="Vendus API Key"
            name="vendusKey"
            type="password"
            autoComplete="off"
            value={vendusKey}
            onChange={(e) => {
              setVendusKey(e.target.value);
              setVendusRegister(null);
              setVendusPaymentMethod(null);
              setPaymentMethods([]);
              setRegisters([]);
            }}
            sx={{ mb: 2 }}
          />
          {vendusKey !== "" && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel style={{ backgroundColor: "#fff" }}>Vendus Register</InputLabel>
                <Select
                  key={registers}
                  value={vendusRegister}
                  onChange={(e) => setVendusRegister(e.target.value)}
                  onOpen={fetchRegisters}
                >
                  {registers.map((register) => (
                    <MenuItem key={register.id} value={register.id}>{register.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel style={{ backgroundColor: "#fff" }}>Vendus Payment Method</InputLabel>
                <Select
                  key={paymentMethods}
                  value={vendusPaymentMethod}
                  onChange={(e) => setVendusPaymentMethod(e.target.value)}
                  onOpen={fetchPaymentMethods}
                >
                  {paymentMethods.map((paymentMethod) => (
                    <MenuItem key={paymentMethod.id} value={paymentMethod.id}>{paymentMethod.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Data & Compliance</Typography>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={showVAT}
                onChange={(e) => setShowVAT(e.target.checked)}
              />
            }
            label="Allow students to add VAT number during registration"
            sx={{ display: "block", mb: 2 }}
          />
          <TextField
            variant="outlined"
            fullWidth
            label="Auto-Anonymize Student Data (months after registration)"
            name="anonymizeAfterMonths"
            type="number"
            autoComplete="off"
            value={anonymizeAfterMonths}
            onChange={(e) => {
              setAnonymizeAfterMonths(e.target.value !== "" ? parseInt(e.target.value) : null);
            }}
            helperText="Runs automatically every 6 hours. This is an irreversible action!"
            sx={{ mb: 1 }}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Custom Student Registration Questions</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              size="small"
              onClick={() => setAddingQuestion(true)}
              sx={{ textTransform: "none" }}
            >
              Add Question
            </Button>
          </Box>
          {extraQuestions.length === 0 ? (
            <Typography variant="body2" sx={{ color: "text.secondary", py: 2 }}>
              No custom questions yet. Click "Add Question" to create one.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {extraQuestions.map((question, index) => (
                <Box
                  key={question.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1.5,
                    backgroundColor: "rgba(19, 33, 68, 0.02)",
                    borderRadius: 1,
                    border: "1px solid rgba(19, 33, 68, 0.08)",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {question.question}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Type: {question.type === "select-multi" ? "Multiple Select" : question.type.charAt(0).toUpperCase() + question.type.slice(1)}
                      {question.required ? " • Required" : " • Optional"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <IconButton size="small" onClick={() => setEditQuestionIndex(index)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        let q = [...extraQuestions];
                        q.splice(index, 1);
                        setExtraQuestions(q);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Box sx={{ mt: 4, display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          size="large"
          onClick={update}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Save Changes
        </Button>
      </Box>

      <AddQuestion
        open={addingQuestion}
        onClose={() => setAddingQuestion(false)}
        onConfirm={(val) => {
          setExtraQuestions([...extraQuestions, val]);
          setAddingQuestion(false);
        }}
      />

      <AddQuestion
        open={editQuestionIndex != null}
        onClose={() => setEditQuestionIndex(null)}
        editQuestion={editQuestionIndex != null ? extraQuestions[editQuestionIndex] : null}
        onConfirm={(val) => {
          let newQuestions = [...extraQuestions];
          newQuestions[editQuestionIndex] = val;
          setExtraQuestions(newQuestions);
          setEditQuestionIndex(null);
        }}
      />
    </Box>
  );
}

export default OrgSettings;
